import {
  useGetAnalyticsCampaigns,
  useGetAnalyticsCohorts,
  useGetAnalyticsGameCorrelation,
  useGetAnalyticsLoyaltyStats,
  useGetAnalyticsRfm,
  useGetAnalyticsRfmCustomers,
  useGetAnalyticsTimeToFirstOrder,
} from '@agos/api-client/customers'
import {
  T,
  formatCents,
  formatCount,
  formatPercent,
  formatDecimal,
  tt,
  useFranchiseModules,
} from '@agos/shared'
import {
  BUTTON_GHOST,
  cn,
  PageHeader,
  Spinner,
  StatCard,
  StatusBadge,
  TabGroup,
} from '@agos/ui'
import {
  Award,
  BarChart3,
  ChefHat,
  Crown,
  Gift,
  Megaphone,
  Repeat,
  Snowflake,
  Sparkles,
  Sprout,
  TrendingUp,
  Users,
  Wallet,
  X,
  XCircle,
  AlertTriangle,
  Clock,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { StatisticsContent } from '../statistics/StatisticsPage'
import { OpsTab } from './OpsTab'
import { RevenueTab } from './RevenueTab'
import {
  campaignSummary,
  cents,
  cohortGrid,
  conversion,
  num,
  orderCampaigns,
  orderSegments,
  orderTiers,
  pointsPopulations,
  recencyMeaning,
  redemptionIsUnmeasured,
  runOutcome,
  runShare,
  segmentShare,
  segmentTotals,
  timeBuckets,
  type AtRiskRow,
  type CampaignRow,
  type CampaignRun,
  type CohortCell,
  type GameStats,
  type LoyaltyStats,
  type RfmResponse,
  type SegmentRow,
} from './analytics-report'

/**
 * `/analytics` — five tabs over fourteen endpoints.
 *
 * Tab one is `/statistics` embedded whole, exactly as legacy does it, and has no tier gate: it is the
 * canonical sales report and is available even where the rest of analytics is locked.
 *
 * The findings this page carries, all measured against the restored dumps:
 *
 * - **FB-112** the margin report cannot be computed and says 100 % instead (`RevenueTab`)
 * - **FB-113** every campaign reports 0 % redemption and cannot report anything else
 * - **FB-114** *Členov 1 108* beside *S bodmi 2 033*, over two different populations
 * - **FB-115** on a custom range six cards silently report the last N days from today (`RevenueTab`)
 * - **FB-116** the SLA range parameters are `dateFrom`/`dateTo`, and its average is contaminated (`OpsTab`)
 * - **FB-117** the custom pizza's topping count is the constant 2 (`RevenueTab`)
 * - **FB-118** the custom pizza's revenue is re-derived when the exact figure is in the payload
 *
 * Ported from legacy `pages/analytics.tsx` (1 734 lines).
 */

type TabId = 'stats' | 'revenue' | 'segments' | 'campaigns' | 'ops'

const SEGMENT_META: Record<
  string,
  { label: string; tone: string; accent: string; icon: typeof Crown; explainer: string }
> = {
  champions: {
    label: tt('Šampióni'),
    tone: 'text-amber-500',
    accent: 'border-l-amber-500',
    icon: Crown,
    explainer: tt('Najnovší, najčastejší, najdrahší. Tých si chráňte.'),
  },
  loyal: {
    label: tt('Verní'),
    tone: 'text-emerald-500',
    accent: 'border-l-emerald-500',
    icon: TrendingUp,
    explainer: tt('Pravidelní zákazníci za posledné dva mesiace.'),
  },
  newCustomers: {
    label: tt('Noví'),
    tone: 'text-blue-500',
    accent: 'border-l-blue-500',
    icon: Sprout,
    explainer: tt('Prvé objednávky za posledné dva týždne.'),
  },
  atRisk: {
    label: tt('Riziko odchodu'),
    tone: 'text-orange-500',
    accent: 'border-l-orange-500',
    icon: AlertTriangle,
    explainer: tt('Predtým aktívni, neobjednali si dva až štyri mesiace.'),
  },
  hibernating: {
    label: tt('Spiaci'),
    tone: 'text-violet-500',
    accent: 'border-l-violet-500',
    icon: Snowflake,
    explainer: tt('Aspoň jedna objednávka, posledná pred viac ako štyrmi mesiacmi.'),
  },
  occasional: {
    label: tt('Príležitostní'),
    tone: 'text-muted-foreground',
    accent: 'border-l-muted',
    icon: Users,
    explainer: tt('Občasní zákazníci bez jasného vzoru.'),
  },
  lost: {
    label: tt('Stratení'),
    tone: 'text-red-500',
    accent: 'border-l-red-500',
    icon: XCircle,
    explainer: tt('Registrovaní bez jedinej dokončenej objednávky.'),
  },
}

/**
 * The campaign types the endpoint answers — fifteen of them.
 *
 * Legacy's map has ten, so `free_delivery_weekend`, `push_blast`, `spin_wheel`, `black_friday` and
 * `welcome_first_order` render as their database keys. All five have nought sends on this data, which
 * is why nobody has noticed.
 */
const CAMPAIGN_LABELS: Record<string, string> = {
  meninova_zlava: tt('Meninová zľava'),
  kuponove_tornado: tt('Kupónové tornádo'),
  kupon_za_vernost: tt('Kupón za vernosť'),
  reaktivacia_neaktivnych: tt('Reaktivácia neaktívnych'),
  cashback: tt('Cashback'),
  happy_hours: tt('Happy hours'),
  click_and_win: tt('Klikni a vyhraj'),
  scratch_card: tt('Stierací los'),
  rebricek: tt('Rebríček'),
  personalizovana_ponuka: tt('Personalizovaná ponuka'),
  free_delivery_weekend: tt('Rozvoz zdarma cez víkend'),
  push_blast: tt('Hromadné push'),
  spin_wheel: tt('Koleso šťastia'),
  black_friday: tt('Black Friday'),
  welcome_first_order: tt('Vitajte - prvá objednávka'),
}

function campaignLabel(type: string | undefined): string {
  return CAMPAIGN_LABELS[type ?? ''] ?? (type ?? '')
}

const TIER_LABELS: Record<string, string> = {
  bronze: 'Bronze',
  silver: 'Silver',
  gold: 'Gold',
  gold_master: 'Gold Master',
  elite_master: 'Elite Master',
}

const TIME_BUCKET_LABELS: Record<string, string> = {
  within_1h: tt('Do hodiny'),
  within_24h: tt('Do 24 hodín'),
  within_7d: tt('Do 7 dní'),
  within_30d: tt('Do 30 dní'),
  after_30d: tt('Po 30 dňoch'),
  never: tt('Nikdy'),
}

export function AnalyticsPage() {
  const [tab, setTab] = useState<TabId>('stats')
  const { isModuleEnabled } = useFranchiseModules()

  // Legacy gates four of the five tabs on a tier module and leaves `stats` open.
  const tabs = (
    [
      { id: 'stats', label: tt('Štatistiky'), icon: BarChart3, module: null },
      { id: 'revenue', label: tt('Tržby'), icon: Wallet, module: 'analytics.revenue' },
      { id: 'segments', label: tt('Segmenty'), icon: Users, module: 'analytics.segments' },
      { id: 'campaigns', label: tt('Kampane'), icon: Megaphone, module: 'analytics.campaigns' },
      { id: 'ops', label: tt('Operácie'), icon: ChefHat, module: 'analytics.ops' },
    ] as const
  ).filter((entry) => entry.module === null || isModuleEnabled(entry.module))

  return (
    <div className="space-y-4">
      <PageHeader
        title={tt('Analytika')}
        description={tt('Segmentácia zákazníkov, tržby, kampane a časy')}
        icon={<BarChart3 className="w-5 h-5" />}
      />

      <TabGroup
        variant="bar"
        tabs={tabs.map((entry) => ({ id: entry.id, label: entry.label, icon: entry.icon }))}
        active={tab}
        onChange={(id) => setTab(id as TabId)}
      />

      {tab === 'stats' && <StatisticsContent hideHeader />}
      {tab === 'revenue' && <RevenueTab />}
      {tab === 'segments' && <SegmentsTab />}
      {tab === 'campaigns' && <CampaignsTab />}
      {tab === 'ops' && <OpsTab />}
    </div>
  )
}

// ─── segments ────────────────────────────────────────────────────────────────

function SegmentsTab() {
  const { data, isLoading } = useGetAnalyticsRfm()
  const response = (data ?? {}) as RfmResponse
  const segments = useMemo(() => orderSegments(response.segments ?? []), [response.segments])
  const atRisk = response.atRisk ?? []
  const totals = segmentTotals(segments)
  const [selected, setSelected] = useState<string | null>(null)

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-segment-totals>
        <StatCard label={tt('Zákazníci spolu')} value={formatCount(totals.customers)} icon={Users} />
        <StatCard
          label={tt('Celkový obrat')}
          value={formatCents(totals.revenueCents)}
          icon={Wallet}
          color="emerald"
        />
        <StatCard
          label={tt('Šampióni')}
          value={formatCount(totals.champions)}
          icon={Crown}
          color="amber"
        />
        <StatCard
          label={tt('Riziko odchodu')}
          value={formatCount(totals.atRisk)}
          icon={AlertTriangle}
          color="red"
        />
      </div>

      <div className="rounded-xl border border-border bg-muted/30 px-4 py-3 text-xs text-muted-foreground space-y-1">
        <p className="font-bold text-foreground">
          <T>Čo tie čísla znamenajú</T>
        </p>
        <p>
          <strong className="text-foreground">Recency</strong>{' '}
          <T>- dní od poslednej objednávky. Čím menej, tým aktívnejší zákazník.</T>
        </p>
        <p>
          <strong className="text-foreground">
            <T>Frekvencia</T>
          </strong>{' '}
          <T>- priemerný počet doručených objednávok na zákazníka v segmente.</T>
        </p>
        <p>
          <strong className="text-foreground">
            <T>Obrat</T>
          </strong>{' '}
          <T>- tržby od zákazníkov v segmente. Kliknutím na kartu zobrazíte zoznam.</T>
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {segments.map((segment) => (
          <SegmentCard
            key={segment.segment}
            segment={segment}
            share={segmentShare(segment, totals)}
            selected={selected === segment.segment}
            onSelect={() =>
              setSelected(selected === segment.segment ? null : (segment.segment ?? null))
            }
          />
        ))}
      </div>

      {selected !== null && (
        <SegmentCustomers segment={selected} onClose={() => setSelected(null)} />
      )}

      {atRisk.length > 0 && <AtRiskList rows={atRisk} />}

      <CohortCard />
      <TimeToFirstOrderCard />
    </div>
  )
}

function SegmentCard({
  segment,
  share,
  selected,
  onSelect,
}: {
  segment: SegmentRow
  share: number
  selected: boolean
  onSelect: () => void
}) {
  const meta = SEGMENT_META[segment.segment ?? ''] ?? SEGMENT_META.occasional!
  const Icon = meta.icon
  const recency = Math.round(num(segment.avg_recency_days))
  const sinceSignup = recencyMeaning(segment) === 'since-signup'

  return (
    <button
      type="button"
      onClick={onSelect}
      data-segment={segment.segment}
      aria-pressed={selected}
      className={cn(
        'text-left rounded-2xl border-l-4 border-y border-r p-4 transition-colors',
        meta.accent,
        selected ? 'bg-primary/5 border-primary/30 ring-2 ring-primary/20' : 'bg-card border-border hover:bg-muted/40',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className={cn('w-4 h-4', meta.tone)} />
          <h3 className={cn('font-bold text-sm', meta.tone)}>{meta.label}</h3>
        </div>
        <span className="text-xs font-bold bg-muted px-2 py-0.5 rounded-full tabular-nums">
          {formatPercent(share, 0)}
        </span>
      </div>
      <p className="text-3xl font-bold mt-2 tabular-nums">{formatCount(segment.count ?? 0)}</p>
      <p className="text-xs text-muted-foreground mt-1">{meta.explainer}</p>
      <dl className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-border text-[10px]">
        <div data-recency={sinceSignup ? 'since-signup' : 'since-order'}>
          {/* `lost` comes back with 988 days, which is age since registration and not recency - they
              never ordered. Saying *od objednávky* there states something untrue. */}
          <dt className="text-muted-foreground uppercase">
            {sinceSignup ? <T>Od registrácie</T> : 'Recency'}
          </dt>
          <dd className="font-bold tabular-nums">
            {formatCount(recency)} <T>dní</T>
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground uppercase">
            <T>Frekv.</T>
          </dt>
          <dd className="font-bold tabular-nums">{formatDecimal(num(segment.avg_frequency), 1)}×</dd>
        </div>
        <div>
          <dt className="text-muted-foreground uppercase">
            <T>Obrat</T>
          </dt>
          <dd className="font-bold tabular-nums">{formatCents(cents(segment.total_revenue))}</dd>
        </div>
      </dl>
    </button>
  )
}

interface SegmentCustomer {
  id?: number
  name?: string | null
  email?: string | null
  phone?: string | null
  is_loyalty?: boolean
  points_balance?: number
  recency_days?: number
  frequency?: number
  monetary?: number | string
}

const SEGMENT_LIST_CAP = 200

function SegmentCustomers({ segment, onClose }: { segment: string; onClose: () => void }) {
  const { data, isLoading } = useGetAnalyticsRfmCustomers({ segment })
  const customers = ((data ?? {}) as { customers?: SegmentCustomer[] }).customers ?? []
  const meta = SEGMENT_META[segment] ?? SEGMENT_META.occasional!

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-segment-list>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
        <h3 className="font-bold">
          <T>Zákazníci v segmente</T>: {meta.label}
        </h3>
        <span className="text-xs text-muted-foreground ml-auto">
          {formatCount(customers.length)}{' '}
          {customers.length >= SEGMENT_LIST_CAP ? (
            /* The endpoint caps at 200 and says so nowhere; a list that stops dead at a round number
               reads as the whole segment. */
            <span data-capped>
              <T>zobrazených - server viac neposkytne</T>
            </span>
          ) : (
            <T>zobrazených</T>
          )}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label={tt('Zatvoriť zoznam')}
          className={cn(BUTTON_GHOST, 'p-1 rounded-lg')}
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {isLoading ? (
        <div className="flex justify-center py-10">
          <Spinner />
        </div>
      ) : customers.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">
          <T>Žiadni zákazníci v tomto segmente.</T>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-muted/50 text-xs">
              <tr>
                <th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase">
                  <T>Zákazník</T>
                </th>
                <th className="px-4 py-2 text-left font-bold text-muted-foreground uppercase">
                  <T>Kontakt</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Dní</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Objedn.</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase">
                  <T>Obrat</T>
                </th>
                <th className="px-4 py-2 text-right font-bold text-muted-foreground uppercase hidden sm:table-cell">
                  <T>Body</T>
                </th>
              </tr>
            </thead>
            <tbody>
              {customers.map((customer) => (
                <tr key={customer.id} className="border-t border-border" data-segment-row>
                  <td className="px-4 py-2 font-medium">
                    {customer.name ?? '-'}
                    {customer.is_loyalty === true && (
                      <StatusBadge label={tt('Vernostný')} color="amber" className="ml-2" />
                    )}
                  </td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">
                    <div className="truncate max-w-[180px]">{customer.email ?? '-'}</div>
                    {customer.phone !== null && customer.phone !== undefined && (
                      <div className="truncate max-w-[180px]">{customer.phone}</div>
                    )}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {/* 999 is the endpoint's "never ordered" sentinel, not a number of days. */}
                    {(customer.recency_days ?? 0) >= 999 ? '-' : formatCount(customer.recency_days ?? 0)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums">
                    {formatCount(customer.frequency ?? 0)}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums font-semibold">
                    {formatCents(cents(customer.monetary))}
                  </td>
                  <td className="px-4 py-2 text-right tabular-nums hidden sm:table-cell">
                    {formatCount(customer.points_balance ?? 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function AtRiskList({ rows }: { rows: AtRiskRow[] }) {
  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-at-risk>
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-orange-500/5">
        <AlertTriangle className="w-4 h-4 text-orange-500" />
        <h3 className="font-bold">
          <T>Najhodnotnejší zákazníci v riziku odchodu</T>
        </h3>
        <span className="ml-auto text-xs text-muted-foreground">
          <T>Pošlite im reaktivačný kupón</T>
        </span>
      </div>
      <div className="divide-y divide-border">
        {rows.map((row) => (
          <div key={row.id} className="px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <div className="flex-1 min-w-0">
              <p className="font-semibold truncate">{row.name ?? '-'}</p>
              <p className="text-xs text-muted-foreground truncate">
                {row.email ?? row.phone ?? '-'}
              </p>
            </div>
            <div className="grid grid-cols-3 sm:flex sm:gap-6 gap-2 text-xs">
              <div className="sm:text-right">
                <p className="text-muted-foreground uppercase text-[10px]">
                  <T>Posledná</T>
                </p>
                <p className="font-bold tabular-nums">
                  {formatCount(row.recency_days ?? 0)} <T>dní</T>
                </p>
              </div>
              <div className="sm:text-right">
                <p className="text-muted-foreground uppercase text-[10px]">
                  <T>Objednávok</T>
                </p>
                <p className="font-bold tabular-nums">{formatCount(row.frequency ?? 0)}</p>
              </div>
              <div className="sm:text-right">
                <p className="text-muted-foreground uppercase text-[10px]">
                  <T>Minul</T>
                </p>
                <p className="font-bold tabular-nums">{formatCents(cents(row.monetary))}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

const COHORT_MONTHS = 12

function CohortCard() {
  const { data, isLoading } = useGetAnalyticsCohorts({ months: COHORT_MONTHS })
  const grid = useMemo(
    () => cohortGrid(((data ?? {}) as { cells?: CohortCell[] }).cells ?? []),
    [data],
  )

  return (
    <div className="bg-card rounded-2xl border border-border p-5" data-cohorts>
      <h3 className="font-bold mb-1 flex items-center gap-2">
        <Repeat className="w-4 h-4 text-primary" /> <T>Vrátenie podľa mesiaca prvej objednávky</T>
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        <T>Koľko percent zákazníkov z každého mesiaca objednalo znova.</T>{' '}
        {/* M0 is 100 % by construction; saying so stops it being read as a result. */}
        <T>M0 je vždy 100 % - je to definícia kohorty, nie výsledok.</T>
      </p>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : grid.cohorts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          <T>Nie sú dostupné dáta pre kohortovú analýzu.</T>
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="text-xs">
            <thead>
              <tr>
                <th className="text-left text-muted-foreground font-bold uppercase pr-3 pb-2">
                  <T>Kohorta</T>
                </th>
                <th className="text-right text-muted-foreground font-bold uppercase pr-3 pb-2">
                  <T>Veľkosť</T>
                </th>
                {Array.from({ length: grid.maxOffset + 1 }, (_, offset) => (
                  <th
                    key={offset}
                    className="text-center text-muted-foreground font-bold uppercase pb-2 px-1 min-w-[40px]"
                  >
                    M{offset}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {grid.cohorts.map((cohort) => (
                <tr key={cohort.month} data-cohort-row>
                  <td className="pr-3 py-1 font-bold tabular-nums">{cohort.month.slice(0, 7)}</td>
                  <td className="pr-3 py-1 text-right tabular-nums text-muted-foreground">
                    {formatCount(cohort.size)}
                  </td>
                  {Array.from({ length: grid.maxOffset + 1 }, (_, offset) => {
                    const percent = cohort.offsets.get(offset)
                    if (percent === undefined) {
                      return (
                        <td key={offset} className="px-1">
                          <div className="h-7" />
                        </td>
                      )
                    }
                    const ratio = percent / 100
                    return (
                      <td key={offset} className="px-1 py-0.5" data-cohort-cell={offset}>
                        <div
                          className="rounded text-center font-bold tabular-nums h-7 flex items-center justify-center"
                          style={{
                            background: `rgba(16, 185, 129, ${0.1 + ratio * 0.85})`,
                            color: ratio > 0.5 ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.65)',
                          }}
                        >
                          {formatPercent(percent, 0)}
                        </div>
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function TimeToFirstOrderCard() {
  const { data, isLoading } = useGetAnalyticsTimeToFirstOrder()
  const buckets = ((data ?? {}) as { buckets?: { bucket?: string; count?: number }[] }).buckets ?? []
  const { rows, total } = timeBuckets(buckets)

  return (
    <div className="bg-card rounded-2xl border border-border p-5" data-time-to-first>
      <h3 className="font-bold mb-1 flex items-center gap-2">
        <Clock className="w-4 h-4 text-primary" /> <T>Čas do prvej objednávky</T>
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        <T>Ako rýchlo sa nový zákazník rozhodne po registrácii.</T>
      </p>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : total === 0 ? (
        <p className="text-sm text-muted-foreground py-6 text-center">
          <T>Žiadne dáta.</T>
        </p>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.bucket} className="space-y-1" data-bucket={row.bucket}>
              <div className="flex items-center justify-between gap-2 text-xs">
                <span className="font-medium">{TIME_BUCKET_LABELS[row.bucket] ?? row.bucket}</span>
                <span className="tabular-nums">
                  <strong>{formatCount(row.count)}</strong>
                  <span className="text-muted-foreground ml-2">{formatPercent(row.percent, 1)}</span>
                </span>
              </div>
              <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                  className={cn(
                    'h-full',
                    row.bucket === 'never'
                      ? 'bg-red-500'
                      : row.bucket === 'after_30d'
                        ? 'bg-violet-500'
                        : 'bg-emerald-500',
                  )}
                  style={{ width: `${row.percent}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── campaigns ───────────────────────────────────────────────────────────────

function CampaignsTab() {
  const { data, isLoading } = useGetAnalyticsCampaigns()
  const response = (data ?? {}) as { campaigns?: CampaignRow[]; recentRuns?: CampaignRun[] }
  const campaigns = useMemo(() => orderCampaigns(response.campaigns ?? []), [response.campaigns])
  const runs = response.recentRuns ?? []
  const summary = campaignSummary(campaigns)
  const unmeasured = redemptionIsUnmeasured(campaigns)

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-campaign-totals>
        <StatCard label={tt('Kampaní')} value={formatCount(summary.campaigns)} icon={Megaphone} />
        <StatCard
          label={tt('Aktívnych')}
          value={formatCount(summary.active)}
          icon={Sparkles}
          color="emerald"
        />
        <StatCard label={tt('Rozoslaných')} value={formatCount(summary.sent)} icon={Megaphone} />
        <StatCard
          label={tt('Využitých kupónov')}
          value={summary.ratePercent === null ? tt('nemerané') : formatPercent(summary.ratePercent, 1)}
          sub={summary.ratePercent === null ? tt('server to nesleduje') : undefined}
          icon={Gift}
          color={summary.ratePercent === null ? 'amber' : 'emerald'}
        />
      </div>

      {/* §9.316, owner Q4 - the panel that stood here is gone, at the owner's second asking.
        *
        * FB-113 put it there to keep 0 % from reading as *nobody used these coupons*, and §10A.99
        * kept it for campaigns whose coupons predate the origin column. Measured 2026-09-14: that is
        * **all fifteen** - 1 655 sends, `tracked_coupons` 0 - so the fix changed the code and never
        * the screen. The owner asked for it removed twice. What remains is the card's own two-word
        * `nemerané`, which is the honest answer without the paragraph; **0 % is still never shown**,
        * because `campaignSummary` answers `null` and the column below says `nemerané` too. */}

      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="px-4 py-3 border-b border-border">
          <h3 className="font-bold">
            <T>Výkon kampaní</T>
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            <T>Rozoslané kupóny podľa typu kampane</T>
          </p>
        </div>
        {campaigns.length === 0 ? (
          <p className="p-6 text-center text-sm text-muted-foreground">
            <T>Zatiaľ žiadne dáta o kampaniach.</T>
          </p>
        ) : (
          <div className="divide-y divide-border">
            {campaigns.map((campaign) => (
              <div
                key={campaign.type}
                className="px-4 py-3 flex items-center gap-4"
                data-campaign={campaign.type}
              >
                <div
                  className={cn(
                    'w-10 h-10 rounded-xl flex items-center justify-center shrink-0',
                    campaign.is_active === true
                      ? 'bg-primary/10 text-primary'
                      : 'bg-muted text-muted-foreground',
                  )}
                >
                  <Megaphone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold" data-campaign-label>
                      {campaignLabel(campaign.type)}
                    </p>
                    <StatusBadge
                      label={campaign.is_active === true ? tt('Aktívna') : tt('Vypnutá')}
                      color={campaign.is_active === true ? 'emerald' : 'zinc'}
                    />
                  </div>
                  {/* Legacy draws a progress bar from the redemption rate, so fifteen empty bars read
                      as fifteen campaigns nobody used. With nothing to measure there is no bar. */}
                  <p className="text-xs text-muted-foreground mt-0.5">
                    <T>Rozoslané</T>: <strong>{formatCount(campaign.sent_count ?? 0)}</strong>
                  </p>
                </div>
                <div className="text-right shrink-0">
                  {unmeasured ? (
                    <p className="text-xs text-amber-500 font-semibold" data-campaign-rate="unmeasured">
                      <T>nemerané</T>
                    </p>
                  ) : (
                    <>
                      <p className="text-sm font-bold tabular-nums">
                        {formatCount(campaign.redeemed_count ?? 0)}/
                        {formatCount(campaign.sent_count ?? 0)}
                      </p>
                      <p className="text-xs text-muted-foreground tabular-nums">
                        {formatPercent(num(campaign.redemption_rate_pct), 1)} <T>využitých</T>
                      </p>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <RecentRuns runs={runs} />
      <LoyaltyCard />
      <GamesCard />
    </div>
  )
}

function RecentRuns({ runs }: { runs: CampaignRun[] }) {
  const refused = runs.filter((run) => runOutcome(run) === 'refused').length

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-runs>
      <div className="px-4 py-3 border-b border-border">
        <h3 className="font-bold">
          <T>Posledné spustenia</T>
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">
          <T>Posledných 30 behov plánovača</T>
          {refused > 0 && (
            <>
              {' · '}
              {/* Legacy colours these red, which sends whoever is on call after a broken campaign. */}
              <span data-refused-count>
                {formatCount(refused)}{' '}
                <T>z nich sa nedá spustiť ručne - to nie je chyba</T>
              </span>
            </>
          )}
        </p>
      </div>
      {runs.length === 0 ? (
        <p className="p-6 text-center text-sm text-muted-foreground">
          <T>Žiadne behy v histórii.</T>
        </p>
      ) : (
        <div className="divide-y divide-border max-h-96 overflow-y-auto">
          {runs.map((run, index) => {
            const outcome = runOutcome(run)
            const share = runShare(run)
            return (
              <div
                key={`${run.campaign_type ?? ''}-${run.ran_at ?? String(index)}`}
                className="px-4 py-2.5 flex items-center gap-3 text-sm"
                data-run-outcome={outcome}
              >
                <span
                  className={cn(
                    'w-2 h-2 rounded-full shrink-0',
                    outcome === 'ok'
                      ? 'bg-emerald-500'
                      : outcome === 'refused'
                        ? 'bg-zinc-400'
                        : 'bg-red-500',
                  )}
                />
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{campaignLabel(run.campaign_type)}</p>
                  {run.error_message !== null && run.error_message !== undefined && run.error_message !== '' && (
                    <p
                      className={cn(
                        'text-xs truncate',
                        outcome === 'refused' ? 'text-muted-foreground' : 'text-destructive',
                      )}
                      title={run.error_message}
                    >
                      {run.error_message}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-3 text-xs shrink-0 text-muted-foreground tabular-nums">
                  <span>
                    {share === null ? (
                      <T>bez adresátov</T>
                    ) : (
                      `${formatCount(run.success_count ?? 0)}/${formatCount(run.recipient_count ?? 0)}`
                    )}
                  </span>
                  <span>{(run.ran_at ?? '').slice(0, 16).replace('T', ' ')}</span>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const LOYALTY_WINDOW_DAYS = 90

function LoyaltyCard() {
  const { data, isLoading } = useGetAnalyticsLoyaltyStats({ days: LOYALTY_WINDOW_DAYS })
  const stats = (data ?? {}) as LoyaltyStats
  const populations = pointsPopulations(stats)
  const tiers = orderTiers(stats.tierDistribution ?? [])

  return (
    <div className="bg-card rounded-2xl border border-border p-5" data-loyalty>
      <h3 className="font-bold mb-1 flex items-center gap-2">
        <Award className="w-4 h-4 text-primary" /> <T>Vernostný program</T>
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        <T>Rozdelenie úrovní a body za posledných 90 dní</T>
      </p>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
            <StatCard label={tt('Členov')} value={formatCount(populations.members)} />
            <StatCard
              // FB-114: this counts every customer with a balance, not every member.
              label={tt('Zákazníkov s bodmi')}
              value={formatCount(populations.withPoints)}
              color={populations.countsDisagree ? 'amber' : 'primary'}
            />
            <StatCard label={tt('Bodov spolu')} value={formatCount(populations.outstanding)} />
            <StatCard
              label={tt('Použitých bodov')}
              value={formatCount(stats.redemptions?.points_redeemed ?? 0)}
              sub={`${formatCount(stats.redemptions?.redemption_orders ?? 0)} ${tt('objednávok')}`}
              color="emerald"
            />
          </div>

          {populations.countsDisagree && (
            <p className="text-[11px] text-amber-500 mb-3" data-points-populations>
              <T>
                Tie dva počty nie sú nad tou istou skupinou: členov je
              </T>{' '}
              <strong>{formatCount(populations.members)}</strong>,{' '}
              <T>ale body má</T> <strong>{formatCount(populations.withPoints)}</strong>{' '}
              <T>zákazníkov - teda aspoň</T>{' '}
              <strong>{formatCount(populations.atLeastNonMembers)}</strong>{' '}
              <T>
                držiteľov bodov nie je členom programu. Aj „Bodov spolu“ je nad tou širšou skupinou.
              </T>
            </p>
          )}

          <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2">
            <T>Rozdelenie úrovní</T>
          </p>
          <div className="flex flex-wrap gap-2" data-tiers>
            {tiers.map((tier) => (
              <div key={tier.level} className="px-3 py-2 rounded-xl bg-muted" data-tier={tier.level}>
                <span className="font-bold text-sm">{TIER_LABELS[tier.level] ?? tier.level}</span>
                <span className="ml-2 font-bold tabular-nums">{formatCount(tier.count)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

function GamesCard() {
  const { data, isLoading } = useGetAnalyticsGameCorrelation({ days: LOYALTY_WINDOW_DAYS })
  const stats = (data ?? {}) as GameStats
  const miniGames = conversion(stats.miniGames)
  const wheel = conversion(stats.wheel)

  return (
    <div className="bg-card rounded-2xl border border-border p-5" data-games>
      <h3 className="font-bold mb-1 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-primary" /> <T>Hry a objednávky</T>
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        <T>Koľko hráčov si objednalo do siedmich dní po hre (posledných 90 dní)</T>
      </p>
      {isLoading ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {(
            [
              { key: 'mini', label: tt('Mini-hra'), icon: Sparkles, side: miniGames, unit: tt('hráčov') },
              { key: 'wheel', label: tt('Koleso šťastia'), icon: Gift, side: wheel, unit: tt('točení') },
            ] as const
          ).map((entry) => (
            <div key={entry.key} className="rounded-xl border border-border p-4" data-game={entry.key}>
              <p className="text-xs font-bold text-muted-foreground uppercase mb-2 flex items-center gap-1.5">
                <entry.icon className="w-3.5 h-3.5" /> {entry.label}
              </p>
              {entry.side.percent === null ? (
                <p className="text-2xl font-bold text-muted-foreground">
                  <T>nemerané</T>
                </p>
              ) : (
                <p className="text-3xl font-bold tabular-nums">{formatPercent(entry.side.percent, 1)}</p>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                <strong>{formatCount(entry.side.converted)}</strong> <T>z</T>{' '}
                <strong>{formatCount(entry.side.players)}</strong> {entry.unit}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
